import {
    Chip,
    FilledInput,
    FormControl,
    FormHelperText, Input,
    InputLabel,
    OutlinedInput
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import * as PropTypes from "prop-types";
import Box from "../Box";
import lang from "../../core/lang";
import downloader from "../../core/downloader";
import React, {useEffect, useState} from "react";
import i18n from "../../core/i18n";
import clsx from "clsx";
import FileDrop from "../FileDrop";
import Button from "../Button";
import fileIO, {FileDescriptor} from "../../core/fileIO";
import {unstable_batchedUpdates} from "react-dom";
import UserEvent from "../../core/UserEvent";
import numeral from "../../core/numeral";
import usePrompt from "../../hook/usePrompt";
import time from "../../core/time";
import {Icon} from "@iconify/react";
import roundDownload from "@iconify/icons-ic/round-download";
import outlineFolder from "@iconify/icons-ic/outline-folder";
import roundClose from "@iconify/icons-ic/round-close";

const MAX_FILE_SIZE_ERROR = 'The file size exceeds the limit of ${size} bytes: ${name}.';
const ZERO_FILE_SIZE_ERROR = 'The file size can not be zero: ${name}.';
const MAX_TOTAL_FILE_SIZE_ERROR = 'The total size of all files exceeds the limit of ${size} bytes.';

const useOutlineInputStyle = makeStyles(() => ({
    root: {
        justifyContent: 'space-between'
    },
    input: {
        display: props => {
            let display;
            if(props.count === 0 || (props.count === 1 && !props.compact)) {
                display = '';
            } else {
                display = 'none';
            }
            return display;
        }
    }
}));

const useChipStyle = makeStyles(theme => ({
    root: {
        color: 'inherit',
        borderColor: 'inherit',
        cursor: 'default',
    },
    deleteIcon: {
        color: 'inherit',
        display: props => props.readOnly ? 'none' : ''
    },
    icon: {
        '&:hover': {
            color: 'color-mix(in srgb, currentColor, white)'
        },
        cursor: 'pointer'
    }
}));

function FileField(props) {
    const { multiple, disabled, readOnly, accept, fullWidth, maxFileSize, maxTotalFileSize, variant,
        helperText, placeholder, size, allowDownload, compact, maxTagLength, disableIndicator, zipFilename} = props;
    const [value, setValue] = useState([]);
    const [descriptors, setDescriptors] = useState([]);
    const [focused, setFocused] = useState(false);
    const prompt = usePrompt();
    const label = lang.isString(props.label) ? i18n.translate(props.label) : props.label;

    useEffect(() => {
        init().catch();
    }, [props.value]);

    const init = async () => {
        const value = lang.isEmpty(props.value) ? [] : (lang.isArray(props.value) ? props.value : [props.value] );
        const descriptors = [];

        for(const source of value) {
            const descriptor = await fetch(source);
            descriptors.push(descriptor);
        }
        unstable_batchedUpdates(() => {
            setValue(value);
            setDescriptors(descriptors);
        });
    }

    const fetch = async source => {
        let descriptor = {};
        if(fileIO.isFileLike(source)) {
            descriptor = source;
        } else {
            descriptor = await downloader.fetchDescriptor(source)
        }
        return descriptor;
    }

    const handleRemove = index => (event) => {
        event.stopPropagation();
        const currentValue = [...value];
        currentValue.splice(index, 1);
        const namedValue = multiple ? currentValue : (currentValue[0] || null);
        props.onChange && props.onChange(new UserEvent({value: namedValue}, event));
        const currentDescriptors = [...descriptors];
        currentDescriptors.splice(index, 1);
        unstable_batchedUpdates(() => {
            setValue(currentValue);
            setDescriptors(currentDescriptors);
        });
    }

    const handleRemoveAll = event => {
        event.stopPropagation();
        const namedValue = multiple ? [] : null;
        props.onChange && props.onChange(new UserEvent({value: namedValue}, event));
        unstable_batchedUpdates(() => {
            setValue([]);
            setDescriptors([]);
        });
    }

    const validateFileSizeConstraint = fileDescriptors => {
        let totalFileSize = descriptors.reduce((sum, descriptor) => {
            return sum+descriptor.size;
        }, 0);
        for(const fileDescriptor of fileDescriptors) {
            const name = fileDescriptor.name;
            if(fileDescriptor.size === 0) {
                throw new Error(i18n.translate(ZERO_FILE_SIZE_ERROR, {name}));
            }
            if(fileDescriptor.size > maxFileSize) {
                const size = numeral.format(maxFileSize, 'compact');
                throw new Error(i18n.translate(MAX_FILE_SIZE_ERROR, {size, name}));
            }
            totalFileSize += fileDescriptor.size;
        }
        if(totalFileSize > maxTotalFileSize) {
            const size = numeral.format(maxTotalFileSize, 'compact');
            throw new Error(i18n.translate(MAX_TOTAL_FILE_SIZE_ERROR, {size}));
        }
    }

    const handleChange = event => {
        let currentValue, currentDescriptors, namedValue;

        try {
            validateFileSizeConstraint(event.descriptors)
        } catch(error) {
            prompt.alert(error.message);
            return;
        }

        if(multiple) {
            currentValue = [...value, ...event.descriptors];
            currentDescriptors = [...descriptors, ...event.descriptors];
            namedValue = currentValue;
        } else {
            currentValue = [event.descriptor];
            currentDescriptors = [event.descriptor];
            namedValue = event.descriptor;
        }

        props.onChange && props.onChange(new UserEvent({value: namedValue}, event));
        unstable_batchedUpdates(() => {
            setValue(currentValue);
            setDescriptors(currentDescriptors);
            setFocused(false);
        });
    }

    const handleDownload = descriptor => async event => {
        event.stopPropagation();
        await downloader.save(descriptor);
    };

    const handleDownloadAll = async event => {
        event.stopPropagation();

        if(descriptors.length === 1) {
            await downloader.save(descriptors[0]);
            return;
        }

        const files = [];

        for(const descriptor of descriptors) {
            const file = await downloader.download(descriptor);
            files.push(file);
        }

        const zip = await fileIO.zip(files);

        let filename;
        if(lang.isEmpty(zipFilename)) {
            const name = lang.isString(props.label) && !lang.isEmpty(props.label) ? i18n.translate(props.label) : props.name;
            const timestamp = time.format(time.now(), 'datetime');
            filename = lang.isEmpty(name) ? `${timestamp}.zip` : `${name}.zip`;
        } else {
            filename = zipFilename.endsWith(".zip") ? zipFilename : `${zipFilename}.zip`;
        }
        await fileIO.save(zip, filename);
    }

    const handleClick = () => {
        // To display the placeholder, we force FormControl to stay focused when the user clicks to open the file dialog.
        setFocused(true)
        // After 10 seconds, set the focused property of FormControl to undefined.
        setTimeout(()=>setFocused(undefined), 10000);
    }

    let title = '', startAdornment = null;
    const count = descriptors.length;
    const outlineInputStyles = useOutlineInputStyle({count, compact});
    const chipClasses = useChipStyle({readOnly});
    if(compact || count > 1) {
        const chips = descriptors.map((descriptor, index) => {
            const downloadTitle = i18n.translate("Download the file")
            const icon = (disabled || !allowDownload || disableIndicator) ? null : (
                <Icon fontSize="1.2rem" icon={roundDownload} onClick={handleDownload(descriptor)} title={downloadTitle} aria-label={downloadTitle} />
            );
            const onDelete = (disabled || readOnly) ? null : handleRemove(index);
            const title = descriptor.name;
            const label = (maxTagLength > 0) && (title.length > maxTagLength) ? `${title.substring(0, maxTagLength)}...` : title;
            return (
                <Chip key={index} label={label} title={title} variant="outlined" classes={chipClasses}
                      onDelete={onDelete} size={variant==='outlined' ? size : 'small'}
                      onClick={event => event.stopPropagation()}
                      icon={icon}/>
            );
        });
        let ratio, totalPaddingY;
        if(variant === 'standard') {
            if(size === 'small') {
                ratio = 0.64;
            } else if(size === 'large') {
                ratio = 1.2
            } else {
                ratio = 1;
            }
            totalPaddingY = 1 * ratio;
        } else if(variant === 'outlined') {
            if(size === 'small') {
                ratio = 0.62;
            } else {
                ratio = 1;
            }
            totalPaddingY = 3.2 * ratio;
        } else {
            if(size === 'small') {
                ratio = 0.75;
            } else {
                ratio = 1;
            }
            totalPaddingY = 4 * ratio;
        }
        let paddingBottom;
        if(variant === 'standard') {
            paddingBottom = 0.8 * ratio;
        } else if(variant === 'outlined') {
            paddingBottom = 1.4 * ratio;
        } else {
            paddingBottom = (label ? 0.6 : 2) * ratio;
        }
        startAdornment = <Box display="flex" flexWrap="wrap" alignItems="flex-end" gap={1} paddingTop={totalPaddingY-paddingBottom} paddingBottom={paddingBottom}>{chips}</Box>
    } else if(count === 1) {
        title = descriptors[0].name;
    }

    const fileDropTitle = i18n.translate("Select a file");
    const fileDropButton = (disabled || readOnly) ? null : (
        <FileDrop onChange={handleChange} multiple={multiple} accept={accept}
                  disabled={disabled || readOnly} >
            <Button size="small" icon={<Icon icon={outlineFolder} />} variant="icon" aria-label={fileDropTitle} title={fileDropTitle} />
        </FileDrop>
    );

    const downloadAllTitle = i18n.translate("Download all files")
    const downloadAllButton = (disabled || !allowDownload) ? null : (
        <Button size="small" onClick={handleDownloadAll} icon={<Icon icon={roundDownload} />} variant="icon" aria-label={downloadAllTitle} title={downloadAllTitle}
                disabled={value.length===0} />
    );
    const removeAllTitle = i18n.translate("Remove all files");
    const removeAllButton = (disabled || readOnly) ? null : (
        <Button size="small" onClick={handleRemoveAll} icon={<Icon icon={roundClose} />} variant="icon"
                disabled={value.length===0}  aria-label={removeAllTitle} title={removeAllTitle} />
    );

    // 在 compact 模式下，至少要顯示 fileDropButton，不然很難辨認這個欄位是用來處理檔案。
    const endAdornment = (
        <Box display="flex" gap={1} >
            {fileDropButton}
            {compact ? null : downloadAllButton}
            {compact ? null : removeAllButton}
        </Box>
    )

    const required = props.required && lang.isNotEmpty(label);

    let InputField;
    if(variant==='outlined') {
        InputField = OutlinedInput;
    } else if(variant==='filled') {
        InputField = FilledInput;
    } else {
        InputField = Input;
    }

    let ariaLabel;
    if(props["aria-label"]) {
        ariaLabel = i18n.translate(props["aria-label"]);
    } else {
        ariaLabel = i18n.translate(lang.isString(props.label) ? props.label : '');
    }

    return (
        <FormControl className={clsx('appfuse-fileField', props.className)} autoFocus={focused}
                     fullWidth={fullWidth} variant={variant} required={required} size={size} disabled={disabled}>
            <FileDrop onChange={handleChange} onClick={handleClick} multiple={multiple} accept={accept}
                      disabled={disabled || readOnly} fullWidth fullHeight >
                {label ? <InputLabel aria-label={ariaLabel}>{label}</InputLabel> : null}
                <InputField classes={outlineInputStyles} label={label} inputProps={{"aria-label": ariaLabel}} readOnly value={title} fullWidth={fullWidth}
                            startAdornment={startAdornment} endAdornment={endAdornment} size={size}
                            placeholder={placeholder} />
            </FileDrop>
            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    )
}

FileField.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(File),
        PropTypes.instanceOf(FileDescriptor),
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(File),
            PropTypes.object,
        ]))
    ]),
    onChange: PropTypes.func,
    onError: PropTypes.func,
    name: PropTypes.string,
    readOnly: PropTypes.bool,
    readOnlyWhen: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
    ]),
    disabled: PropTypes.bool,
    disabledWhen: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
    ]),
    required: PropTypes.bool,
    status: PropTypes.string,
    placeholder: PropTypes.string,
    variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
    size: PropTypes.oneOf(['large', 'medium', 'small']),
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    multiple: PropTypes.bool,
    accept: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
    ]),
    maxFileSize: PropTypes.number,
    maxTotalFileSize: PropTypes.number,
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    height: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    helperText: PropTypes.string,
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    "aria-label": PropTypes.string,
    allowDownload: PropTypes.bool,
    compact: PropTypes.bool,
    // Tag 文字的最大長度
    maxTagLength: PropTypes.number,
    disableIndicator: PropTypes.bool,
    zipFilename: PropTypes.string
};

FileField.defaultProps = {
    multiple:  false,
    maxFileSize: 10*1000*1000,
    maxTotalFileSize: 50*1000*1000,
    variant: 'standard',
    allowDownload: true,
    compact: false,
    maxTagLength: 32,
    disableIndicator: false
};

export default FileField;